/**
 * SimpleChannelTestPage — shared Roomgrid page for the "message" channels
 * (SMS, WhatsApp, Telegram). Email adds attachments/rich text but follows
 * the same section order; the push channels have their own pages.
 *
 * Sections: Account & application → Message → Recipients → Scheduling →
 * Delivery options (priority + webhook). CTA in the shell footer.
 */

import React, { useState } from "react";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { Grid, MenuItem, TextField } from "@mui/material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { sendTestNotification } from "../services/testService";
import { buildTestPayload } from "../transforms/buildTestPayload";
import {
  createEmptyTestFormValues,
  MESSAGE_PRIORITY_CHOICES,
} from "../constants";
import type {
  NotificationChannel,
  NotificationTestFormValues,
} from "../types/notificationTest";
import AccountAppPicker from "./sections/AccountAppPicker";
import RecipientsSection from "./sections/RecipientsSection";
import MessageSection from "./sections/MessageSection";
import SchedulingSection from "./sections/SchedulingSection";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

interface SimpleChannelTestPageProps {
  channel: Extract<NotificationChannel, "sms" | "whatsapp" | "telegram">;
}

const CHANNEL_KEYS: Record<
  SimpleChannelTestPageProps["channel"],
  { title: string; sent: string; failed: string }
> = {
  sms: {
    title: "resources.test.sms.title",
    sent: "resources.test.notifications.smsSent",
    failed: "resources.test.notifications.smsSendFailed",
  },
  whatsapp: {
    title: "resources.test.whatsapp.title",
    sent: "resources.test.notifications.whatsappSent",
    failed: "resources.test.notifications.whatsappSendFailed",
  },
  telegram: {
    title: "resources.test.telegram.title",
    sent: "resources.test.notifications.telegramSent",
    failed: "resources.test.notifications.telegramSendFailed",
  },
};

export const SimpleChannelTestPage: React.FC<SimpleChannelTestPageProps> = ({
  channel,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const keys = CHANNEL_KEYS[channel];

  const methods = useForm<NotificationTestFormValues>({
    defaultValues: createEmptyTestFormValues(),
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit = async (
    values: NotificationTestFormValues,
  ): Promise<void> => {
    setSubmitting(true);
    try {
      const body = buildTestPayload(channel, values);
      const response = await sendTestNotification(channel, body);
      if (response.success) {
        notify(keys.sent, { type: "success" });
        methods.reset(createEmptyTestFormValues());
      } else {
        notify(response.message ?? translate(keys.failed), { type: "error" });
      }
    } catch (err: unknown) {
      notify(extractErrorMessage(err, keys.failed, translate), {
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey={keys.title}
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <MessageSection channel={channel} />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.recipients", {
            _: "Recipients",
          })}
        >
          <RecipientsSection channel={channel} />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.scheduling", {
            _: "Scheduling",
          })}
        >
          <SchedulingSection />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.delivery", {
            _: "Delivery options",
          })}
          last
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="messagePriority"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.messagePriorityRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label={translate("resources.test.fields.messagePriority")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  >
                    {MESSAGE_PRIORITY_CHOICES.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {translate(c.labelKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="webhookUrl"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.fields.webhookUrl")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default SimpleChannelTestPage;
